import { ElementRef, HostListener, Injectable, Renderer2 } from "@angular/core";
import { Data, Router } from "@angular/router";
import { CatchErrorService } from "@core/catch-error.service";
import { throwError, combineLatest } from "rxjs";
import { catchError, filter, map, take } from "rxjs/operators";
import { CheckoutFlowService } from "src/app/services/checkout-flow.service";
import { PaymentService } from "src/app/services/payment.service";
import { AddCardComponent } from "../add-card.component";
import { StoreCheckoutFlowHost, StoreCheckoutFlowService } from "../../services/store-checkout-flow.service";

@Injectable()
export class CheckoutFramesService {
  private framesInitRetryCount = 0;
  private readonly framesInitMaxRetry = 3;
  private readonly framesInitRetryDelay = 500;
  private checkoutFlowInitSubscription?: import("rxjs").Subscription;
  private checkoutFlowInitialized = false;

  constructor(
    private catchService: CatchErrorService,
    private paymentService: PaymentService,
    private renderer: Renderer2,
    private storeCheckoutFlowService: StoreCheckoutFlowService,
    private checkoutFlowService: CheckoutFlowService,
    private router: Router,
  ) {}

  @HostListener("window:message", ["$event"])
  onMessage(e): void {
    if (e && e?.data && e?.data?.for === "paymentDone") {
      // To check for active current session or manually login using token
    }
  }

  public async initializeFrames(componentContext: AddCardComponent, keys: Data, language: string): Promise<void> {
    try {
      componentContext.keys = keys;
      componentContext.language = language;
      await this.storeCheckoutFlowService.mountStoreCheckoutFlow(this.toFlowHost(componentContext));
      this.framesInitRetryCount = 0;
    } catch (error) {
      if ((error as Data)?.code === "checkout_flow_mount_cancelled") { return; }
      if ((error as Data)?.code !== "checkout_flow_ready_timeout" && this.framesInitRetryCount < this.framesInitMaxRetry) {
        this.framesInitRetryCount++;
        setTimeout(() => {
          void this.initializeFrames(componentContext, keys, language);
        }, this.framesInitRetryDelay);
      } else {
        componentContext.errorMessage = "Failed to initialize payment form. Please try again later.";
        componentContext.showCheckOutComSpinner = false;
        this.catchService.logError("Failed to initialize checkout flow", error);
      }
    }
  }

  public onCardValidationChanged(componentContext: AddCardComponent): void {
    componentContext.isButtonDisabled = !componentContext.checkoutFlowReady;
  }

  public onValidationChanged(_event: Data, componentContext: AddCardComponent): void {
    componentContext.isButtonDisabled = !componentContext.checkoutFlowReady;
  }

  public getErrorMessage(element: string): string {
    const errors: { [key: string]: string } = {
      "card-number": "Please enter a valid card number",
      "expiry-date": "Please enter a valid expiry date",
      cvv: "Please enter a valid CVV code",
    };

    return errors[element] || "An error occurred.";
  }

  public onCardTokenizationFailed(): void {
    // Flow handles tokenization internally; no-op for compatibility.
  }

  public onSubmit(event: Event): void {
    event.preventDefault();
    this.storeCheckoutFlowService.submitPayment();
  }

  public checkAndUpdateStylesForIframe(checkOutIframe: ElementRef): void {
    try {
      const computedStyle = window.getComputedStyle(checkOutIframe?.nativeElement);
      if (computedStyle?.getPropertyValue("left") === "-10000px") {
        checkOutIframe.nativeElement.style["left"] = "unset !important";
      }
    } catch (error) {
      return error;
    }
  }

  loadCheckoutApiAndInitialize(componentContext: AddCardComponent, queryParam) {
    if (this.handleCheckoutFlowRedirectReturn(componentContext)) {
      return;
    }

    componentContext.showCheckOutComSpinner = true;
    componentContext.errorMessage = "Loading secure card form...";
    componentContext.changeDetRef.detectChanges();

    this.paymentService
      ?.getCheckoutApiDetails(queryParam)
      ?.pipe(
        catchError((err) => {
          return throwError(() => err);
        }),
      )
      .subscribe(
        (res: Data) => {
          componentContext.keys = res.data;
          if (!res.data?.publicKey) {
            return;
          }

          componentContext.isPaymill = false;
          componentContext.isCheckoutCom = true;
          componentContext.changeDetRef.detectChanges();
          this.scheduleCheckoutFlowMount(componentContext);
        },
        (err) => {
          return err;
        },
      );
  }

  cancelCheckoutFlowInitSchedule(): void {
    this.checkoutFlowInitSubscription?.unsubscribe();
    this.checkoutFlowInitSubscription = undefined;
  }

  private scheduleCheckoutFlowMount(componentContext: AddCardComponent): void {
    if (this.checkoutFlowInitialized) {
      return;
    }

    const tryMount = (): boolean => {
      const purchaseDetail = componentContext.purchaseDetail$?.value;
      if (!purchaseDetail || !componentContext.pathString || !this.shouldUseCheckoutCom(purchaseDetail)) {
        return false;
      }

      this.checkoutFlowInitialized = true;
      this.checkoutFlowInitSubscription?.unsubscribe();
      componentContext.changeDetRef.detectChanges();
      requestAnimationFrame(() => {
        void componentContext.initializeFrames();
      });
      return true;
    };

    if (tryMount()) {
      return;
    }

    this.checkoutFlowInitSubscription?.unsubscribe();
    this.checkoutFlowInitSubscription = combineLatest([
      componentContext.purchaseDetail$.pipe(
        filter((purchaseDetail) => !!purchaseDetail && this.shouldUseCheckoutCom(purchaseDetail)),
      ),
      componentContext.actRoute.params.pipe(
        map((params) => params?.time as string | undefined),
        filter((time) => !!time),
      ),
    ])
      .pipe(take(1))
      .subscribe(([, pathString]) => {
        if (!componentContext.pathString) {
          componentContext.pathString = pathString;
        }
        tryMount();
      });
  }

  private shouldUseCheckoutCom(purchaseDetail: Data | null): boolean {
    const pType = String(purchaseDetail?.p_type ?? purchaseDetail?.offers?.p_type ?? "21");
    return pType === "21";
  }

  public async unloadPaymentWidget(): Promise<void> {
    try {
      this.storeCheckoutFlowService.unmount();
      if (window.wpwl !== undefined && window.wpwl?.unload !== undefined) {
        await window.wpwl.unload();

        const scripts = document.querySelectorAll('script[src*="static.min.js"]');
        scripts.forEach((script) => {
          this.renderer.removeChild(document.body, script);
        });
      }
    } catch (error) {
      return error;
    }
  }

  unmount(): void {
    this.cancelCheckoutFlowInitSchedule();
    this.storeCheckoutFlowService.unmount();
  }

  isCheckoutLoadingMessage(message: string): boolean {
    return this.storeCheckoutFlowService.isCheckoutLoadingMessage(message);
  }

  handleCheckoutFlowRedirectReturn(componentContext: AddCardComponent): boolean {
    const redirectReturn = this.checkoutFlowService.parseRedirectReturn(
      componentContext.actRoute.snapshot.queryParams ?? {},
    );
    if (!redirectReturn.isRedirectReturn) {
      return false;
    }

    if (redirectReturn.logId) {
      componentContext.pathString = redirectReturn.logId;
      this.paymentService.syncCheckoutFlowStatus({ logId: redirectReturn.logId });
    } else if (!componentContext.pathString) {
      componentContext.pathString = componentContext.actRoute.snapshot?.params?.["time"];
    }

    if (redirectReturn.userId) {
      this.paymentService.checkoutFlowUserId$.next(redirectReturn.userId);
    }

    // Router state is lost after Checkout.com Flow redirect; restore purchase detail so
    // top-up keeps isTopUp and is not redirected to syndicate (HATDEV-97335).
    this.restorePurchaseDetailAfterRedirect(componentContext);

    componentContext.isCheckoutCom = true;
    componentContext.isPaymill = false;
    componentContext.addNewCardInProgress = true;
    componentContext.showCheckOutComSpinner = true;
    componentContext.cardTokenizedCalled = true;
    componentContext.changeDetRef.detectChanges();

    if (redirectReturn.status === "success") {
      componentContext.onCheckoutFlowPaymentCompleted();
      this.storeCheckoutFlowService.clearPersistedPurchaseDetail();
      this.clearCheckoutFlowReturnQueryParams(componentContext);
      return true;
    }

    if (redirectReturn.status === "fail") {
      componentContext.errorMessage = "Payment failed, please try again.";
      componentContext.showCheckOutComSpinner = false;
      componentContext.addNewCardInProgress = false;
      componentContext.changeDetRef.detectChanges();
      this.clearCheckoutFlowReturnQueryParams(componentContext);
      return true;
    }

    this.clearCheckoutFlowReturnQueryParams(componentContext);
    return true;
  }

  private clearCheckoutFlowReturnQueryParams(componentContext: AddCardComponent): void {
    const cleaned = this.checkoutFlowService.stripRedirectReturnParams({
      ...(componentContext.actRoute.snapshot.queryParams ?? {}),
    });
    void this.router.navigate([], {
      relativeTo: componentContext.actRoute,
      queryParams: cleaned,
      replaceUrl: true,
    });
  }

  private restorePurchaseDetailAfterRedirect(componentContext: AddCardComponent): void {
    if (componentContext.purchaseDetail$?.value) {
      return;
    }

    const restored =
      this.storeCheckoutFlowService.restorePurchaseDetailForRedirect(componentContext.pathString) ||
      (componentContext.storePaymentData as Data) ||
      (typeof history !== "undefined" ? (history.state as Data) : null);

    if (restored && Object.keys(restored).length > 0) {
      componentContext.storePaymentData = restored;
      componentContext.purchaseDetail$.next(restored);
      componentContext.receivedData = restored;
    }
  }

  private toFlowHost(componentContext: AddCardComponent): StoreCheckoutFlowHost {
    return {
      keys: componentContext.keys,
      language: componentContext.language,
      paymentMode: componentContext.paymentMode,
      pathString: componentContext.pathString,
      checkOutComData: componentContext.checkOutComData,
      purchaseDetail$: componentContext.purchaseDetail$,
      userData: componentContext.userData,
      preferredCurrency: componentContext.preferredCurrency,
      selectedDiscountValue: componentContext.selectedDiscountValue,
      updatedPrizeValue: componentContext.updatedPrizeValue,
      isCheckoutComform: componentContext.isCheckoutComform,
      isButtonDisabled: componentContext.isButtonDisabled,
      showCheckOutComSpinner: componentContext.showCheckOutComSpinner,
      errorMessage: componentContext.errorMessage,
      checkoutFlowReady: componentContext.checkoutFlowReady,
      cardTokenizedCalled: componentContext.cardTokenizedCalled,
      addNewCardInProgress: componentContext.addNewCardInProgress,
      getCookie: (name) => componentContext.getCookie(name),
      getCheckOutComResponseSubscription: componentContext.getCheckOutComResponseSubscription,
      onFlowPaymentCompleted: () => componentContext.onCheckoutFlowPaymentCompleted(),
      onFlowReady: () => {
        componentContext.checkoutFlowReady = true;
        componentContext.showCheckOutComSpinner = false;
        componentContext.isButtonDisabled = false;
        if (this.storeCheckoutFlowService.isCheckoutLoadingMessage(componentContext.errorMessage)) {
          componentContext.errorMessage = "";
        }
        componentContext.changeDetRef.detectChanges();
      },
    };
  }
}

declare var window: Data;
